// The owner-only permissions given to one person before roles held them (gap #18:
// permissions.listGrants; BL-137 gives no new ones), for the owner, above the people: each permission in
// words (its key on hover), who holds it and until when. Taking one back stays on the person's row; this
// is the overview. The same for roles limited to some places (roles.listScopes): who, which role, and
// where it reaches.

import { useEffect, useState } from 'react';
import { client } from '../api.ts';
import { scrollFocus } from '../shared/scrollFocus.ts';
import { reachText, untilText, type Grant, type RoleScope } from './grants-model.ts';

export { reachText, untilText, type Grant, type RoleScope };

export function GivenPermissions({
  people,
  permissions,
  timeZone,
}: {
  /** The People list: names by staff id; a new list (after a grant changes) reads the grants again. */
  people: readonly { staffId: string; displayName: string }[];
  /** Owner-only permissions with their descriptions (roles.list). */
  permissions: readonly { key: string; description: string }[] | null;
  timeZone: string;
}) {
  const [grants, setGrants] = useState<Grant[] | null>(null);
  useEffect(() => {
    void client
      .call<{ grants: Grant[] }>('permissions.listGrants', {})
      .then((r) => setGrants(r.grants))
      .catch(() => setGrants([]));
  }, [people]);
  if (!grants?.length) return null;
  const who = (id: string) => people.find((p) => p.staffId === id)?.displayName ?? 'Someone';
  const what = (key: string) => permissions?.find((p) => p.key === key)?.description ?? key;
  return (
    <section className="sheet card given" aria-label="Given permissions">
      <h2>Given before roles</h2>
      <p className="muted small">
        Owner-only permissions given to one person before roles held them. Each runs to its end date; take one
        back on their row. Nothing new is given this way: give them a role.
      </p>
      <div className="tablewrap" ref={scrollFocus}>
        <table className="t" aria-label="Given permissions">
          <thead>
            <tr>
              <th>Person</th>
              <th>Permission</th>
              <th>How long</th>
            </tr>
          </thead>
          <tbody>
            {grants.map((g) => (
              <tr key={`${g.staffId}:${g.permission}`}>
                <td>{who(g.staffId)}</td>
                <td title={g.permission}>{what(g.permission)}</td>
                <td className="nowrap">{untilText(g.expiresAt, timeZone)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function LimitedRoles({
  people,
  roleNames,
  unitName,
}: {
  /** The People list: names by staff id; a new list (after a limit changes) reads the limits again. */
  people: readonly { staffId: string; displayName: string }[];
  roleNames: Record<string, string>;
  unitName: (id: string) => string;
}) {
  const [scopes, setScopes] = useState<RoleScope[] | null>(null);
  useEffect(() => {
    void client
      .call<{ scopes: RoleScope[] }>('roles.listScopes', {})
      .then((r) => setScopes(r.scopes))
      .catch(() => setScopes([]));
  }, [people]);
  if (!scopes?.length) return null;
  const who = (id: string) => people.find((p) => p.staffId === id)?.displayName ?? 'Someone';
  return (
    <section className="sheet card given" aria-label="Roles limited to some places">
      <h2>Roles limited to some places</h2>
      <p className="muted small">
        Each reaches only the jobs, visits and invoices there; every other role covers the whole company.
        Change one on the person’s row.
      </p>
      <div className="tablewrap" ref={scrollFocus}>
        <table className="t" aria-label="Roles limited to some places">
          <thead>
            <tr>
              <th>Person</th>
              <th>Role</th>
              <th>Reaches only</th>
            </tr>
          </thead>
          <tbody>
            {scopes.map((s) => (
              <tr key={`${s.staffId}:${s.roleKey}`}>
                <td>{who(s.staffId)}</td>
                <td>{roleNames[s.roleKey] ?? s.roleKey}</td>
                <td>{reachText(s, unitName)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
